export class WebApplicationStore<Schema> {
  constructor(
    private namespace = '',
    private storageArea: Storage = localStorage,
  ) {}

  private getKeyPrefix() {
    return this.namespace + '__'
  }

  getKeys(): string[] {
    const keyPrefix = this.getKeyPrefix()
    return Object.keys(this.storageArea)
      .filter((key) => key.startsWith(keyPrefix))
      .map((key) => key.slice(keyPrefix.length))
  }

  getItem<K extends keyof Schema & string>(key: K): Schema[K] | null {
    const keyPrefix = this.getKeyPrefix()
    const namespacedKey = keyPrefix + key
    const value = this.storageArea.getItem(namespacedKey)

    if (!value) return null

    return parsePrimitive(value) as Schema[K]
  }

  setItem<K extends keyof Schema & string>(
    key: K,
    value: Schema[K] | null,
  ): void {
    if (value === null) {
      this.removeItem(key)
    } else {
      const keyPrefix = this.getKeyPrefix()
      const namespacedKey = keyPrefix + key
      const valueString =
        typeof value === 'string' ? value : JSON.stringify(value)
      this.storageArea.setItem(namespacedKey, valueString)
    }
  }

  removeItem<K extends keyof Schema & string>(key: K): void {
    const keyPrefix = this.getKeyPrefix()
    const namespacedKey = keyPrefix + key
    this.storageArea.removeItem(namespacedKey)
  }

  clear(): void {
    const keyPrefix = this.getKeyPrefix()
    const keysToClear = Object.keys(this.storageArea).filter((key) =>
      key.startsWith(keyPrefix),
    )
    keysToClear.forEach((key) => this.storageArea.removeItem(key))
  }
}

const parsePrimitive = (value: string) => {
  const isBoolean = /true|false/i.test(value)
  const isNumber = !isNaN(Number(value))

  if (isBoolean || isNumber) {
    return JSON.parse(value) as boolean | number
  }

  return value
}
